import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, Input } from '@agms/ui'
import { forgotPasswordApi } from '../lib/api'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import agmsLogo from '../assets/logos/agms-logo-on-white.svg'

function fragmentCredentials(hash: string): { token: string; email: string } {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  const params = new URLSearchParams(raw)

  return {
    token: params.get('token') ?? '',
    email: params.get('email') ?? '',
  }
}

export function PasswordResetPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [fragment] = useState(() => fragmentCredentials(location.hash))
  const token = fragment.token
  const email = fragment.email
  const hasEmail = email.length > 0
  const formReady = hasEmail && token.length > 0

  useEffect(() => {
    if (!location.hash) {
      return
    }

    const nextUrl = `${window.location.pathname}${window.location.search}`
    window.history.replaceState(window.history.state, '', nextUrl)
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true },
    )
  }, [location.hash, location.pathname, location.search, navigate])
  const [error, setError] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)

  useEffect(() => {
    document.title = 'Reset your password · AGMS CRM'
  }, [])

  const mutation = useMutation({
    mutationFn: ({ password, passwordConfirmation }: { password: string; passwordConfirmation: string }) => (
      forgotPasswordApi.reset(email, token, password, passwordConfirmation)
    ),
    onSuccess: () => setCompleted(true),
    onError: (err) => setError(apiErrorMessage(err, 'Unable to reset password.')),
  })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    const form = new FormData(event.currentTarget)
    mutation.mutate({
      password: String(form.get('password') ?? ''),
      passwordConfirmation: String(form.get('password_confirmation') ?? ''),
    })
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md overflow-hidden">
        <div className="h-1 bg-accent-500" />
        <CardHeader className="items-center text-center">
          <img src={agmsLogo} alt="AGMS" className="h-12 w-auto" />
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Reset your password</h1>
          <p className="mt-2 text-sm text-gray-600">
            {hasEmail
              ? 'Choose a new password. This link works once.'
              : 'AGMS CRM password reset'}
          </p>
        </CardHeader>
        <CardContent>
          {!formReady && <p className="text-sm text-red-600" role="alert">Password reset token is missing.</p>}
          {completed ? (
            <div className="space-y-4 text-center">
              <p className="text-sm text-emerald-700" role="status">Password reset. You can now sign in with your new password.</p>
              <Link to="/login">
                <Button type="button" className="w-full">Go to sign in</Button>
              </Link>
            </div>
          ) : formReady ? (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <Input name="password" label="New password" type="password" autoComplete="new-password" minLength={12} required />
              <Input name="password_confirmation" label="Confirm password" type="password" autoComplete="new-password" minLength={12} required />
              {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
              <Button type="submit" className="w-full" isLoading={mutation.isPending}>
                Reset password
              </Button>
            </form>
          ) : null}
          <Link to="/login" className="mt-4 block text-center text-sm text-blue-600 hover:text-blue-800">
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    </main>
  )
}
